import { get, post, put, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse } from '../../types/api';
import {
  BackupDestination,
  BackupOAuthStatus,
  BackupOverview,
  BackupProvider,
  BackupRecord,
  BackupQueueStatus,
  BackupRun,
  BackupSchedule,
  BackupSyncResult,
  BackupEnqueueResult,
  BackupJobRun,
  RestoreEnqueueResult,
  DataModule,
  RestoreUploadInspection,
  RestoreUploadPayload,
  RestoreConflictStrategy,
  RestoreMode,
  RestorePreview,
  BackupEncryptionStatus,
  StorageHealth,
} from '../../types/backup';

export interface CreateBackupPayload {
  destinationId?: string;
  includeDatabase?: boolean;
  includeAttachments?: boolean;
  modules?: string[];
}

export interface UpdateSchedulePayload {
  isEnabled?: boolean;
  frequency?: string;
  time?: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  destinationId?: string | null;
  includeDatabase?: boolean;
  includeAttachments?: boolean;
  modules?: string[];
  keepLast?: number;
}

export interface SaveDestinationPayload {
  label?: string;
  isEnabled?: boolean;
  isDefault?: boolean;
  config?: Record<string, any>;
}

export interface RestorePayload {
  confirm: boolean;
  includeDatabase?: boolean;
  includeAttachments?: boolean;
  safetyBackup?: boolean;
  modules?: string[];
  /** How to treat data that already exists: overwrite (default) or skip. */
  conflictStrategy?: RestoreConflictStrategy;
  /** Explicit restore mode (merge / skip / replace / clean). */
  mode?: RestoreMode;
  /** Typed confirmation for a clean restore ("DELETE ALL DATA"). */
  confirmText?: string;
  /** Read-only preview: compute add/update/delete counts without restoring. */
  dryRun?: boolean;
}

export const backupService = {
  getOverview: (): Promise<ApiResponse<BackupOverview>> =>
    get<BackupOverview>('/system/backup/overview'),

  listDataModules: (): Promise<ApiResponse<DataModule[]>> =>
    get<DataModule[]>('/system/backup/modules'),

  listBackups: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<BackupRecord[]>> =>
    get<BackupRecord[]>('/system/backup/backups', params),

  createBackup: (
    payload: CreateBackupPayload,
  ): Promise<ApiResponse<BackupEnqueueResult>> =>
    post<BackupEnqueueResult>('/system/backup/backups', payload),

  /** Polls the live progress of a queued/running backup. */
  getRunProgress: (id: string): Promise<ApiResponse<BackupRun>> =>
    get<BackupRun>(`/system/backup/backups/${id}/progress`),

  /** Current BACKUP_ENCRYPTION_KEY health (never returns the key itself). */
  getEncryptionStatus: (): Promise<ApiResponse<BackupEncryptionStatus>> =>
    get<BackupEncryptionStatus>('/system/backup/encryption'),

  /** Re-wraps a stored archive with the key currently configured on the server. */
  reencryptBackup: (
    id: string,
  ): Promise<
    ApiResponse<{
      record: BackupRecord;
      entries: number;
      sizeBytes: string;
      checksum: string;
    }>
  > => post(`/system/backup/backups/${id}/reencrypt`),

  pauseBackup: (id: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/backups/${id}/pause`),

  resumeBackup: (id: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/backups/${id}/resume`),

  cancelBackup: (id: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/backups/${id}/cancel`),

  destroyJob: (id: string): Promise<ApiResponse<{ destroyed: boolean }>> =>
    del<{ destroyed: boolean }>(`/system/backup/backups/jobs/${id}`),

  /** Re-queues a failed backup job. */
  retryBackup: (id: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/backups/${id}/retry`),

  getQueueStatus: (): Promise<ApiResponse<BackupQueueStatus>> =>
    get<BackupQueueStatus>('/system/backup/queue'),

  getStorageHealth: (): Promise<ApiResponse<StorageHealth>> =>
    get<StorageHealth>('/system/backup/storage-health'),

  pauseQueue: (): Promise<ApiResponse<{ paused: boolean }>> =>
    post<{ paused: boolean }>('/system/backup/queue/pause'),

  resumeQueue: (): Promise<ApiResponse<{ paused: boolean }>> =>
    post<{ paused: boolean }>('/system/backup/queue/resume'),

  deleteBackup: (id: string): Promise<ApiResponse<{ deleted: boolean }>> =>
    del<{ deleted: boolean }>(`/system/backup/backups/${id}`),

  /** Deletes several backups (and their stored files) in one request. */
  deleteBackups: (
    ids: string[],
  ): Promise<
    ApiResponse<{
      deleted: number;
      failed: Array<{ id: string; message: string }>;
    }>
  > =>
    post<{
      deleted: number;
      failed: Array<{ id: string; message: string }>;
    }>('/system/backup/backups/bulk-delete', { ids }),

  verifyBackup: (
    id: string,
  ): Promise<ApiResponse<{ valid: boolean; message: string }>> =>
    post<{ valid: boolean; message: string }>(
      `/system/backup/backups/${id}/verify`,
    ),

  restoreBackup: (
    id: string,
    payload: RestorePayload,
  ): Promise<ApiResponse<RestoreEnqueueResult>> =>
    post<RestoreEnqueueResult>(`/system/backup/backups/${id}/restore`, payload),

  /** Polls the live progress of a restore job. */
  getRestoreRun: (jobId: string): Promise<ApiResponse<BackupJobRun>> =>
    get<BackupJobRun>(`/system/backup/restores/${jobId}`),

  pauseRestore: (jobId: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/restores/${jobId}/pause`),

  resumeRestore: (jobId: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/restores/${jobId}/resume`),

  cancelRestore: (jobId: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/restores/${jobId}/cancel`),

  destroyRestoreJob: (
    jobId: string,
  ): Promise<ApiResponse<{ destroyed: boolean }>> =>
    del<{ destroyed: boolean }>(`/system/backup/restores/jobs/${jobId}`),

  /** Re-queues a failed restore job. */
  retryRestore: (jobId: string): Promise<ApiResponse<{ state: string }>> =>
    post<{ state: string }>(`/system/backup/restores/${jobId}/retry`),

  /** Uploads a `.hmsbkup` and returns its contents for a restore preview. */
  inspectRestoreUpload: async (
    file: File,
  ): Promise<ApiResponse<RestoreUploadInspection>> => {
    const form = new FormData();
    form.append('file', file);
    const response = await httpService.post<ApiResponse<RestoreUploadInspection>>(
      '/system/backup/restore-upload/inspect',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 },
    );
    return response.data;
  },

  /** Queues a previously inspected uploaded archive for restore. */
  restoreUpload: async (
    payload: RestoreUploadPayload,
  ): Promise<ApiResponse<RestoreEnqueueResult>> => {
    const response = await httpService.post<ApiResponse<RestoreEnqueueResult>>(
      '/system/backup/restore-upload',
      payload,
      { timeout: 0 },
    );
    return response.data;
  },

  listDestinations: (): Promise<ApiResponse<BackupDestination[]>> =>
    get<BackupDestination[]>('/system/backup/destinations'),

  /**
   * Asks the server to scan the destinations for archives it doesn't know
   * about yet (e.g. backups uploaded by another installation) and register
   * them so they can be restored.
   */
  syncDestinations: (
    destinationId?: string,
  ): Promise<ApiResponse<BackupSyncResult>> =>
    post<BackupSyncResult>('/system/backup/destinations/sync', {
      destinationId,
    }),

  saveDestination: (
    provider: BackupProvider,
    payload: SaveDestinationPayload,
  ): Promise<ApiResponse<BackupDestination>> =>
    put<BackupDestination>(`/system/backup/destinations/${provider}`, payload),

  testDestination: (
    provider: BackupProvider,
    config?: Record<string, any>,
  ): Promise<ApiResponse<{ ok: boolean; message: string }>> =>
    post<{ ok: boolean; message: string }>(
      `/system/backup/destinations/${provider}/test`,
      { config },
    ),

  deleteDestination: (
    provider: BackupProvider,
  ): Promise<ApiResponse<{ deleted: boolean }>> =>
    del<{ deleted: boolean }>(`/system/backup/destinations/${provider}`),

  getAuthUrl: (
    provider: BackupProvider,
  ): Promise<ApiResponse<{ url: string; redirectUri: string }>> =>
    get<{ url: string; redirectUri: string }>(
      `/system/backup/destinations/${provider}/auth-url`,
    ),

  getOAuthStatus: (): Promise<
    ApiResponse<Record<string, BackupOAuthStatus>>
  > => get<Record<string, BackupOAuthStatus>>('/system/backup/oauth/status'),

  /**
   * Dry-run for a stored backup: per-table add/update/delete counts, computed
   * without touching the database.
   */
  previewRestore: (
    id: string,
    payload: RestorePayload,
  ): Promise<ApiResponse<RestorePreview>> =>
    post<RestorePreview>(`/system/backup/backups/${id}/restore`, {
      ...payload,
      dryRun: true,
    }),

  /** Dry-run for an inspected upload. */
  previewUploadRestore: (
    payload: RestoreUploadPayload,
  ): Promise<ApiResponse<RestorePreview>> =>
    post<RestorePreview>('/system/backup/restore-upload', {
      ...payload,
      dryRun: true,
    }),

  getSchedule: (): Promise<ApiResponse<BackupSchedule>> =>
    get<BackupSchedule>('/system/backup/schedule'),

  updateSchedule: (
    payload: UpdateSchedulePayload,
  ): Promise<ApiResponse<BackupSchedule>> =>
    put<BackupSchedule>('/system/backup/schedule', payload),

  /**
   * Runs the saved schedule immediately through the scheduled pipeline, so the
   * run is recorded as `scheduled` and updates the schedule's stats/retention.
   */
  runScheduleNow: (): Promise<ApiResponse<BackupEnqueueResult>> =>
    post<BackupEnqueueResult>('/system/backup/schedule/run'),

  /** Fetches a stored backup as a blob via the authenticated HTTP client. */
  getBackupBlob: async (record: BackupRecord): Promise<Blob> => {
    const response = await httpService.get(
      `/system/backup/backups/${record.id}/download`,
      {
        responseType: 'blob',
        // Archives can take far longer than the default 30s on a slow link;
        // aborting mid-download is a common source of unusable backup files.
        timeout: 0,
      },
    );
    return new Blob([response.data], { type: 'application/octet-stream' });
  },

  /** Downloads a backup via the authenticated HTTP client as a blob. */
  downloadBackup: async (record: BackupRecord): Promise<void> => {
    const blob = await backupService.getBackupBlob(record);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = record.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};
